import React, { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Spinner from '@components/Spinner';
import { MySavedDealButtonType } from 'src/controllers/MyAccount';
import useTranslations from '@hooks/useTranslations';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { IDealProductType, IMySavedDeal } from 'src/redux/user/user.duck.interface';
import { theme } from 'src/styles/theme';
import { renderAvailable } from './components/CarAvailable';
import DealCashPrice from './components/DealCashPrice';
import DealFinancePrice from './components/DealFinancePrice';
import { createMySavesDealSteps } from './components/dealSteps.utils';
import { getImageUrl, renderImage } from './components/Image';
import { renderOrderExpiration } from './components/OrderExpiration';
import { renderCarName } from './components/renderCarName';
import { renderDealStepsFlow } from './components/renderDealStapsFlow';
import { renderOverlay } from './components/renderOverlay';
import Icon, { Icons } from '../../Icon/index';
import { MySummary } from '../../MySummary/MySummary';
import { formatDateByLang, formatLeadTime } from '@utils/Date.utils';
import { OfferDemandedWrapper } from './components/OfferDemandedWrapper';
import { IBaseCarOffer } from 'src/interfaces/Car';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import { IS_B2C, isBrandAC, isBrandDS, MARKET, MARKETS, ModalVersion } from 'src/constants/main';
import Button from '@components/Button';
import Link from '@components/Link';

interface IMySavedDealProps {
    mySavedDeal: IMySavedDeal;

    handleActivateDeal(): void;

    handleDeleteDeal(): void;

    handleGetPersonalisedOffer(): void;

    btnActivateType: MySavedDealButtonType;
    btnDeleteType: MySavedDealButtonType;
    btnGetPersonalisedOfferType: MySavedDealButtonType;
    id?: string;
}

const MySavedDeal: FC<IMySavedDealProps> = ({
    mySavedDeal,
    handleActivateDeal,
    handleDeleteDeal,
    handleGetPersonalisedOffer,
    btnActivateType,
    btnDeleteType,
    btnGetPersonalisedOfferType,
    id,
}: IMySavedDealProps) => {
    const [isExtendedViewOpen, setIsExtendedViewOpen] = useState(MARKET === MARKETS.FR);
    const dispatch = useDispatch();
    const { t } = useTranslations();

    const getDisabledState = useCallback(
        (type: MySavedDealButtonType) =>
            type === MySavedDealButtonType.DISABLED || type === MySavedDealButtonType.PROGRESS,
        []
    );

    const isDealerLayerEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_DEALER_LAYER_ENABLED);

    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);

    const isActivateButtonDisabled = getDisabledState(btnActivateType);

    const isDeleteButtonDisabled = getDisabledState(btnDeleteType);

    const isGetPersonalisedOfferButtonDisabled = getDisabledState(btnGetPersonalisedOfferType);

    const isDealOffersEnabled = !!mySavedDeal.fullProductConfiguration?.offers?.find(
        ({ dealerLayerEnabled }: IBaseCarOffer) => dealerLayerEnabled
    );

    const shouldDisplayGetPersonalisedOffer = isDealOffersEnabled && isDealerLayerEnabled;

    const isOfferParent = mySavedDeal.productType === IDealProductType.PARENT;

    const shouldDisplayContinueButtons =
        mySavedDeal.productType === IDealProductType.CONFIGURABLE && shouldDisplayGetPersonalisedOffer;

    const hasChildren = mySavedDeal.children?.length > 0;

    const displayPartExchange = useMemo(() => !!mySavedDeal.partExchangeId, [mySavedDeal]);

    const partExchangeId = useMemo(() => mySavedDeal.partExchangeId, [mySavedDeal]);

    const toggleExtendedView = useCallback(() => {
        setIsExtendedViewOpen(!isExtendedViewOpen);
        isExtendedViewOpen
            ? dispatch(UIDuck.closeCharacteristics())
            : dispatch(UIDuck.openCharacteristics({ data: {}, modalType: '' }));
    }, [isExtendedViewOpen]);

    const openModal = () => {
        dispatch(
            UIDuck.openModal({
                data: { data: {} },
                callbacks: { onClick: handleDeleteDeal },
                modalType: UIDuck.MODAL_TYPES.DELETE_DEAL,
                modalVersion: ModalVersion.v2,
            })
        );
    };

    return (
        <div id={id}>
            {shouldDisplayGetPersonalisedOffer && renderOrderExpiration(mySavedDeal, t)}
            <div className="deal">
                {renderOverlay(mySavedDeal, t)}
                <div className="deal-content">
                    <div className="car-header">
                        <div className="title">{renderCarName(mySavedDeal.fullProductConfiguration)}</div>
                        <div className="price-and-date">
                            <div className="price-container">
                                {mySavedDeal.financeQuoteId ? (
                                    <DealFinancePrice
                                        id={mySavedDeal.financeQuoteId}
                                        fullProductConfiguration={mySavedDeal.fullProductConfiguration}
                                        dealerId={mySavedDeal.dealerId}
                                    />
                                ) : (
                                    <DealCashPrice
                                        prices={mySavedDeal.fullProductConfiguration.pricesV2}
                                        fullProductConfiguration={mySavedDeal.fullProductConfiguration}
                                    />
                                )}
                            </div>
                            <div className="date">
                                <span className="date-clock-icon">
                                    <Icon name={isBrandAC ? Icons.ClockSolid : Icons.Clock} height={16} width={16} />
                                </span>
                                <span>{formatLeadTime(mySavedDeal.fullProductConfiguration.leadTimeDate)}</span>
                            </div>
                        </div>
                    </div>
                    <div className="content">
                        {renderImage(getImageUrl(mySavedDeal.fullProductConfiguration))}
                        {renderDealStepsFlow(createMySavesDealSteps(t, mySavedDeal))}
                    </div>

                    {shouldDisplayGetPersonalisedOffer ? (
                        shouldDisplayContinueButtons && (
                            <div className="buttons-ec41">
                                <Link
                                    primary
                                    withArrowIcon
                                    label={t('myaccount.mySaves.removeDeal')}
                                    onClick={openModal}
                                    dataTestId={`TESTING_DELETE_OFFER_${mySavedDeal.token}`}
                                    marginBottom={20}
                                />
                                <div className="buttons-box-ec41">
                                    <Button
                                        primary
                                        disabled={isGetPersonalisedOfferButtonDisabled}
                                        onClick={handleActivateDeal}
                                        data-testid={`TESTING_CONTINUE_WITH_DEAL_${mySavedDeal.token}`}
                                    >
                                        {t('myaccount.continue')}
                                    </Button>
                                    <Button
                                        secondary
                                        disabled={isGetPersonalisedOfferButtonDisabled}
                                        onClick={handleGetPersonalisedOffer}
                                        data-testid={`TESTING_DEMAND_OFFER_BTN_${mySavedDeal.token}`}
                                        marginTop={20}
                                    >
                                        {t('myaccount.mySaves.getPersonalisedOffer')}
                                    </Button>
                                </div>
                            </div>
                        )
                    ) : (
                        <>
                            <div className={`buttons ${isSOLEnabled ? 'deal-buttons-sol' : ''}`}>
                                <div className="buttons-box">
                                    <Button secondary onClick={toggleExtendedView} disabled={isDeleteButtonDisabled}>
                                        {t('checkout.myAccount.viewAllDetails')}
                                    </Button>
                                    <Link
                                        primary
                                        withArrowIcon
                                        label={t('myaccount.mySaves.removeDeal')}
                                        onClick={openModal}
                                        dataTestId={`TESTING_DELETE_OFFER_${mySavedDeal.token}`}
                                        marginTop={15}
                                        marginBottom={15}
                                    />
                                    <Button
                                        primary
                                        onClick={handleActivateDeal}
                                        disabled={isActivateButtonDisabled}
                                        data-testid={`TESTING_CONTINUE_WITH_DEAL_${mySavedDeal.token}`}
                                    >
                                        {t('myaccount.mySaves.activateDeal')}
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
                {isExtendedViewOpen && (
                    <MySummary
                        carDetails={{
                            ...mySavedDeal.fullProductConfiguration,
                            productType: mySavedDeal.productType,
                            extraFields: mySavedDeal.extraFields,
                        }}
                        dealerId={mySavedDeal.dealerId}
                        showFinanceWidget
                        displayPx={displayPartExchange}
                        journeyType={mySavedDeal.journeyType}
                        financeQuoteId={mySavedDeal.financeQuoteId}
                        partExchangeId={partExchangeId}
                        offers={mySavedDeal.children || []}
                        deal={mySavedDeal}
                    />
                )}
                {isOfferParent && !hasChildren && (
                    <OfferDemandedWrapper>
                        <div className="offer_demanded_line" />
                        <span className="offer_demanded_title">
                            {IS_B2C
                                ? t('myaccount.mySaves.personalisedOfferTitle.b2c')
                                : t('myaccount.mySaves.personalisedOfferTitle')}
                        </span>
                        {t('myaccount.mySaves.personalisedOfferDate', {
                            date: formatDateByLang(mySavedDeal.created),
                        })}
                    </OfferDemandedWrapper>
                )}
            </div>
        </div>
    );
};

export default MySavedDeal;
